import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type ReviewItem, type SellerRating } from '../lib/api';
import { useMyReview, useSaveReview } from '../lib/queries';
import { confirmDialog, haptic } from '../lib/telegram';
import { Button, Field, Sheet, Spinner, errorMessage, formatDate, useToast } from './ui';

const REVIEW_MAX = 1000;

/** Звёзды оценки (только показ). Дробная часть округляется до половины звезды визуально — через ширину заливки. */
export function Stars({ value, size = 14 }: { value: number; size?: number }) {
  const pct = Math.max(0, Math.min(100, (value / 5) * 100));
  return (
    <span className="stars" style={{ fontSize: size }} aria-hidden>
      <span className="stars-base">★★★★★</span>
      <span className="stars-fill" style={{ width: `${pct}%` }}>
        ★★★★★
      </span>
    </span>
  );
}

/** «★ 4.8 · 12 отзывов» — или «Пока нет отзывов». */
export function RatingLine({ rating, compact }: { rating: SellerRating | null | undefined; compact?: boolean }) {
  const { t } = useTranslation();
  if (!rating || !rating.count) return compact ? null : <span className="tiny muted">{t('reviews.none')}</span>;
  return (
    <span className="rating-line" aria-label={t('reviews.ariaRating', { avg: rating.average.toFixed(1), n: rating.count })}>
      <Stars value={rating.average} />
      <strong>{rating.average.toFixed(1)}</strong>
      <span className="muted">· {t('reviews.count', { count: rating.count })}</span>
    </span>
  );
}

export function ReviewCard({ r, onReport }: { r: ReviewItem; onReport?: () => void }) {
  const { t } = useTranslation();
  return (
    <article className="review">
      <div className="review-head">
        <span className="avatar avatar-sm" aria-hidden>
          {(r.authorName || '?').slice(0, 1).toUpperCase()}
        </span>
        <div className="review-who">
          <strong>{r.authorName || t('reviews.anonymous')}</strong>
          <span className="tiny muted">
            {formatDate(r.createdAt)}
            {r.edited ? ` · ${t('reviews.edited')}` : ''}
          </span>
        </div>
        <Stars value={r.rating} />
      </div>
      {r.text ? <p className="review-text">{r.text}</p> : null}
      <div className="review-foot">
        {r.listingTitle ? <span className="tiny muted review-listing">{t('reviews.about', { title: r.listingTitle })}</span> : <span />}
        {onReport ? (
          <button type="button" className="link tiny" onClick={onReport}>
            {t('reviews.report')}
          </button>
        ) : null}
      </div>
    </article>
  );
}

/** Оставить, изменить или удалить свой отзыв о продавце. */
export function ReviewSheet({ open, onClose, sellerId, sellerName }: { open: boolean; onClose: () => void; sellerId: string; sellerName: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const mine = useMyReview(sellerId, open);
  const save = useSaveReview(sellerId);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');

  useEffect(() => {
    if (!open) return;
    setRating(mine.data?.review?.rating ?? 0);
    setText(mine.data?.review?.text ?? '');
  }, [open, mine.data]);

  const submit = () =>
    save.mutate(
      { rating, text: text.trim() },
      {
        onSuccess: () => {
          haptic.success();
          toast(t('reviews.saved'), 'success');
          onClose();
        },
        onError: (e) => {
          haptic.error();
          toast(errorMessage(t, e), 'error');
        },
      },
    );

  const remove = async () => {
    if (!(await confirmDialog(t('reviews.confirmDelete')))) return;
    save.mutate(null, { onSuccess: () => { toast(t('reviews.deleted'), 'success'); onClose(); }, onError: (e) => toast(errorMessage(t, e), 'error') });
  };

  const existing = mine.data?.review;
  return (
    <Sheet open={open} onClose={onClose} title={existing ? t('reviews.editTitle') : t('reviews.leaveTitle', { name: sellerName })}>
      {mine.isLoading ? (
        <Spinner />
      ) : mine.data && !mine.data.eligible ? (
        <p className="muted">{mine.data.reason === 'own' ? t('reviews.cannotOwn') : t('reviews.needChat')}</p>
      ) : (
        <div className="stack">
          {existing?.hidden ? <div className="notice">{t('reviews.hiddenNotice')}</div> : null}
          <div className="star-input" role="radiogroup" aria-label={t('reviews.yourRating')}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={rating === n}
                aria-label={t('reviews.starsN', { count: n })}
                className={n <= rating ? 'on' : ''}
                onClick={() => {
                  haptic.select();
                  setRating(n);
                }}
              >
                ★
              </button>
            ))}
          </div>
          <p className="tiny muted center">{rating ? t(`reviews.scale.${rating}`) : t('reviews.tapStar')}</p>
          <Field label={t('reviews.text')} hint={t('reviews.textHint')}>
            <textarea className="input" rows={4} maxLength={REVIEW_MAX} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('reviews.placeholder')} />
          </Field>
          <Button block loading={save.isPending} disabled={!rating} onClick={submit}>
            {existing ? t('common.save') : t('reviews.publish')}
          </Button>
          {existing ? (
            <Button variant="ghost" block onClick={() => void remove()}>
              {t('reviews.delete')}
            </Button>
          ) : null}
          <p className="tiny muted">{t('reviews.rules')}</p>
        </div>
      )}
    </Sheet>
  );
}

/** Жалоба на отзыв (рассматривает модератор). */
export function ReviewReportSheet({ reviewId, onClose }: { reviewId: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  useEffect(() => setReason(''), [reviewId]);
  const send = async () => {
    if (!reviewId) return;
    setSending(true);
    try {
      await api(`/reviews/${reviewId}/report`, { method: 'POST', json: { reason: reason.trim() } });
      haptic.success();
      toast(t('reviews.reported'), 'success');
      onClose();
    } catch (e) {
      toast(errorMessage(t, e), 'error');
    } finally {
      setSending(false);
    }
  };
  return (
    <Sheet open={Boolean(reviewId)} onClose={onClose} title={t('reviews.reportTitle')}>
      <div className="stack">
        <Field label={t('reviews.reportReason')}>
          <textarea className="input" rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('reviews.reportPlaceholder')} />
        </Field>
        <Button block loading={sending} disabled={!reason.trim()} onClick={() => void send()}>
          {t('reviews.reportSend')}
        </Button>
      </div>
    </Sheet>
  );
}

/** «На площадке 1 год» / «3 месяца» / «с этого месяца» — по дате регистрации. */
export function useMemberFor() {
  const { t } = useTranslation();
  return (iso: string) => {
    const months = Math.floor((Date.now() - new Date(iso).getTime()) / (30.44 * 86400_000));
    if (months >= 12) return t('seller.memberYears', { count: Math.floor(months / 12) });
    if (months >= 1) return t('seller.memberMonths', { count: months });
    return t('seller.memberNew');
  };
}

